<template>
<modal class="section-titles-modal" size="medium" @close="close">
	<template slot="header">
		<h3>
			<span> {{ $t(`${titleHeader} Stock`) }}</span>
		</h3>
	</template>
	<template slot="body">
		<div class="row">
			<!-- Warehouse Label -->
			<div class="col-12" v-if="warehouse">
				<div class="form-group">
					<label><b>{{ $t("Warehouse") }}</b></label>
					<div>{{ warehouse.label }}</div>
				</div>
			</div>
			<div class="col-md-6">
				<div class="form-group">
					<label class="font-weight-bold">
						<span>{{ $t("Stock") }} </span>
						<span v-if="type === 'sync'">{{ $t("Quantity") }}</span>
						<span class="text-success" v-if="type === 'increase'">{{ $t("Increment") }}</span>
						<span class="text-danger" v-if="type === 'decrease'">{{ $t("Decrement") }}</span>
					</label>
					<input
						type="number"
						class="form-control"
						placeholder="0"
						@input="inputQuantity($event, 'quantity')"
						v-model="quantity"
					/>
					<div class="pc-input-desc" v-if="type === 'sync'">
						<p>{{ $t("Input your real product stock here") }}</p>
					</div>
				</div>
			</div>
			<div class="col-md-6">
				<div class="form-group">
					<label><b>{{ $t("Description") }}</b></label>
					<textarea
						class="form-control"
						placeholder="Stock alteration description"
						v-model="stockDescription"
					/>
				</div>
			</div>
		</div>
	</template>
	<template slot="footer">
		<!-- Cancel -->
		<button type="button" class="btn btn-light" @click="close">
			<span>{{ $t("Cancel") }}</span>
		</button>
		<!-- Save - Multi Warehouse -->
		<button
			class="btn btn-success"
			:class="{ 'is-loading': isUpdatingStock }"
			@click="update"
		>
			<i class="material-icons">save</i>
			<span>{{ $t("Save") }}</span>
		</button>
	</template>
</modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import modal from '@/components/Modal.vue';
import homepageApi from '@/api/homepage';
import productInventoryApi from '@/api/product-inventory';
import productsApi from '@/api/products';
import { generateCombinations, setMaximumStock } from '@/lib/helper';

export default {
	components: {
		modal,
	},
	props: {
		formType: {
			type: String,
			default: () => '',
		},
		type: {
			type: String,
			default: () => '',
		},
		name: {
			type: String,
			default: () => '',
		},
		stock: {
			type: [Number, String],
			default: () => 0,
		},
		description: {
			type: String,
			default: () => '',
		},
		warehouse: {
			type: Object,
			default: () => {},
		},
		isMultiWarehouse: {
			type: Boolean,
			default: () => false,
		},
		stockVariant: {
			type: String,
			default: () => '',
		},
		productId: {
			type: String,
			default: '',
		},
		product: {
			type: Object,
		},
	},
	data() {
		return {
			isUpdatingStock: false,
			quantity: 0,
			stockDesc: '',
			maxLength: 7,
			dataStock: {},
		};
	},
	computed: {
		...mapGetters({
			// productType: 'productWarehouse/type',
			variants: 'productWarehouse/variants',
			productStock: 'productWarehouse/stock',
		}),
		productType() {
			return this.product.type;
		},
		variations() {
			const isVariableProduct = this.product.type === 'variable';
			const variations = isVariableProduct
				? generateCombinations(this.product.variations ?? [])
				: [];
			return variations;
		},
		stockQty: {
			get() {
				let value = 0;
				if (this.formType === 'single') {
					if (this.type === 'sync' && this.productType === 'variable') {
						value = this.stock;
					} else if (this.type === 'sync' && this.productType === 'simple') {
						value = this.product?.quantity ?? 0;
					}
				} else if (this.formType !== 'single') {
					if (this.type === 'sync' && this.productType === 'variable') {
						value = this.stock;
					} else if (this.type === 'sync' && this.productType === 'simple') {
						value = this.warehouse.quantity;
					}
				}
				return value;
			},
			set(value) {
				const payload = {
					warehouse_id: this.warehouse?._id ?? 0,
					name: this.name,
					value,
				};
				if (this.formType === 'single') {
					const params = {
						...this.product.stock,
						quantity: value,
					};
					this.setStock(params);
				} else if (this.productType === 'simple' && this.type === 'sync') {
					this.updateStockSimple(payload);
				} else if (this.productType === 'variable' && this.type === 'sync') {
					this.updateStockVariable(payload);
				}
				this.quantity = value;
			},
		},
		titleHeader() {
			let title = '';
			if (this.type === 'sync') {
				title = 'Adjust';
			} else if (this.type === 'increase') {
				title = 'Add';
			} else if (this.type === 'decrease') {
				title = 'reduce';
			}
			return title;
		},
		stockDescription: {
			get() {
				return this.description;
			},
			set(value) {
				this.stockDesc = value;
			},
		},
	},
	watch: {},
	created() {
		if (this.type === 'sync') {
			this.quantity = this.stock;
		}
	},
	methods: {
		...mapActions({
			updateStockVariable: 'productWarehouse/updateStockVariable',
			setVariantStock: 'productWarehouse/setVariantStock',
			updateStockSimple: 'productWarehouse/updateStockSimple',
			setStockCheckedProductsCount: 'setStockCheckedProductsCount',
			// setStock: 'productWarehouse/setStock',
		}),
		updateStock() {},
		inputDetection(input) {
			if (input[0] === '0') {
				return false;
			}
			return true;
		},
		inputQuantity(event) {
			const value = event.target.value;
			if (Number.isNaN(parseInt(value))) {
				this.quantity = 1;
			} else if (value < 0) {
				this.quantity = 1;
			} else if (value >= 0 && value.length > this.maxLength) {
				this.quantity = setMaximumStock(value, this.maxLength);
			} else if (!this.inputDetection(value)) {
				this.quantity = 1;
			}
		},
		update() {
			if (this.formType === 'single') {
				this.updateProductStock();
			} else {
				this.updateWarehouseStock();
			}
		},
		close() {
			this.$emit('close');
		},
		/**
		 * Update Product Stock
		 *
		 * @param {'increase' | 'decrease' | 'sync'} type Type of stock update
		 * @param {number} quantity Stock quantity
		 * @param {string} description Stock change description
		 * @param {string} [variation] Variation name
		 */
		updateWarehouseStock() {
			this.isUpdatingStock = true;

			const id = this.warehouse?._id ?? '';

			const params = {
				type: this.type,
				quantity: this.quantity,
				description: this.stockDesc,
			};
			params.variation = this.name;

			const callback = (response) => {
				this.close();
				const productInventory = response.data;
				if (this.productType === 'variable') {
					const savedVariations = {
						...productInventory,
						variations: productInventory.variations ?? [],
					};
					const mapper = (variation) => {
						const quantity = 0;
						if (variation.name === true) {
							variation.name = this.name;
						}
						return {
							name: variation.join('||'),
							quantity,
						};
					};
					const productVariations = this.variations.map(mapper);
					const products = {
						...savedVariations,
						stockVariant: productVariations,
					};
					this.$emit('set-stock', products, this.type);
					this.setVariantStock(products);
				} else {
					this.$emit('set-stock', productInventory, this.type);
				}

				this.isUpdatingStock = false;

				this.$notify({
					group: 'app',
					text: 'Stock successfully updated.',
					type: 'success',
				});
			};

			const errorCallback = (e) => {
				console.log(e);
				this.isUpdatingStock = false;
				this.$notify({
					group: 'app',
					text: 'Stock update occurred an error, please try again.',
					type: 'danger',
				});
			};
			productInventoryApi.update(id, params, callback, errorCallback);
		},
		/**
		 * Update Product Stock
		 *
		 * @private {string} productId Product ID
		 * @param {'increase' | 'decrease' | 'sync'} type Type of stock update
		 * @param {number} quantity Stock quantity
		 * @param {string} description Stock change description
		 * @param {string} [variation] Variation name
		 */
		updateProductStock() {
			const productId = this.$route.params.id ?? this.productId;
			this.isUpdatingStock = true;
			const params = {
				type: this.type,
				quantity: this.quantity ?? 0,
				description: this.stockDesc,
			};
			if (this.stockVariant) {
				params.variation = this.stockVariant;
			}

			const callback = (response) => {
				if (this.productType === 'variable') {
					const variationsPrices = response.data.variations.prices;
					this.updateVariations(variationsPrices);
				} else {
					const quantity = response.data.stock.quantity ?? 0;
					this.$emit('set-stock', quantity);
				}

				this.close();
				this.isUpdatingStock = false;

				this.$notify({
					group: 'app',
					text: 'Stock successfully updated.',
					type: 'success',
				});

				// this.resetStockUpdate();

				// Hide popup
				this.isUpdateStockVisible = false;
				this.fetchStockCheckedProductsCount();
			};
			const errorCallback = () => {
				this.isUpdatingStock = false;
				this.$notify({
					group: 'app',
					text: 'Stock update occurred an error, please try again.',
					type: 'danger',
				});
			};
			productsApi.updateStock(productId, params, callback, errorCallback);
		},
		fetchStockCheckedProductsCount() {
			const callback = (response) => {
				const count = response.data;
				this.setStockCheckedProductsCount(count);
			};
			homepageApi.getStockCheckedProducts(callback);
		},
		updateVariations(prices) {
			this.$emit('update-variations', prices);
		},
		setStock(stock) {
			this.dataStock = stock;
		},
	},
};
</script>
